import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Header } from '@/ui/components/Header';
import { Footer } from '@/ui/components/Footer';
import { MobilePageHeader } from '@/ui/components/MobilePageHeader';
import { SEO, metaFrom } from '@/ui/components/SEO';
import {
    FileText, CreditCard, Gift, RotateCcw, CalendarClock,
    XCircle, Star, ShieldAlert, Lock, Scale, RefreshCw,
    Settings2, ShieldCheck, Languages, Scissors, ClipboardList,
} from 'lucide-react';

/**
 * Page Conditions Générales de Vente d'abonnement — /cgv (US 6.13)
 * SSI Sentinel Premium (Stripe web / Google Play Billing Android).
 * ⚠️ Contient des champs à compléter à la création de la micro-entreprise (dénomination, SIRET).
 */
const SubscriptionTermsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isNative = Capacitor.isNativePlatform();

    const sections = [
        { icon: <FileText className="w-5 h-5" />, key: 'object', items: ['scope', 'editor', 'nature'] },
        { icon: <Settings2 className="w-5 h-5" />, key: 'features', items: ['functionalities', 'requirements', 'interoperability', 'noResultGuarantee'] },
        { icon: <CreditCard className="w-5 h-5" />, key: 'pricing', items: ['plans', 'taxes', 'providers', 'changes'] },
        { icon: <Gift className="w-5 h-5" />, key: 'trial', items: ['duration', 'oneShot', 'conversion', 'cancelBefore', 'antiAbuse'] },
        { icon: <RotateCcw className="w-5 h-5" />, key: 'withdrawal', items: ['right', 'waiver', 'consequence'] },
        { icon: <ShieldCheck className="w-5 h-5" />, key: 'conformity', items: ['statutory', 'scope', 'updates', 'remedy'] },
        { icon: <CalendarClock className="w-5 h-5" />, key: 'duration', items: ['renewal', 'effect'] },
        { icon: <XCircle className="w-5 h-5" />, key: 'cancellation', items: ['how', 'effect', 'noRefund'] },
        { icon: <Star className="w-5 h-5" />, key: 'founders', items: ['gracious', 'window', 'noPayment', 'notice'] },
        { icon: <ShieldAlert className="w-5 h-5" />, key: 'liability', items: ['forecast', 'availability', 'force'] },
        { icon: <Lock className="w-5 h-5" />, key: 'data', items: ['privacy'] },
        { icon: <RefreshCw className="w-5 h-5" />, key: 'changes', items: ['revision', 'notice'] },
        { icon: <Languages className="w-5 h-5" />, key: 'language', items: ['text'] },
        { icon: <Scissors className="w-5 h-5" />, key: 'severability', items: ['text'] },
        { icon: <ClipboardList className="w-5 h-5" />, key: 'general', items: ['proof', 'license', 'nonWaiver'] },
        { icon: <Scale className="w-5 h-5" />, key: 'law', items: ['applicable', 'mediation', 'contact'] },
    ];

    return (
        <>
            {/* Titre et intro déjà traduits : pas de clés seo.* dédiées. */}
            <SEO title={t('cgv.page.title')} description={metaFrom(t('cgv.page.intro'))} />
            {isNative
                ? <MobilePageHeader title={t('cgv.page.mobileTitle')} icon="receipt_long" showBackButton onBack={() => navigate(-1)} />
                : <Header />
            }
            <main className={`min-h-screen bg-[#0f2023] text-white ${isNative ? 'pt-20 pb-24' : 'pt-28 pb-20'}`}>
                <div className="max-w-3xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm mb-4">
                            <FileText className="w-4 h-4" />
                            {t('cgv.page.badge')}
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-3">{t('cgv.page.title')}</h1>
                        <p className="text-slate-400 text-sm">{t('cgv.page.lastUpdate')}</p>
                    </div>

                    <p className="text-slate-300 leading-relaxed mb-10 text-sm">{t('cgv.page.intro')}</p>

                    <div className="space-y-8">
                        {sections.map((section, idx) => (
                            <section key={idx} className="bg-white/[0.03] border border-white/10 rounded-xl p-6">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="text-primary">{section.icon}</div>
                                    <h2 className="text-lg font-semibold">{t(`cgv.page.${section.key}.title`)}</h2>
                                </div>
                                <ul className="space-y-2">
                                    {section.items.map((item, i) => (
                                        <li key={i} className="text-slate-400 text-sm leading-relaxed flex items-start gap-2">
                                            <span className="text-primary/60 mt-1.5">•</span>
                                            <span>{t(`cgv.page.${section.key}.${item}`)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>

                    <p className="text-slate-400 text-xs leading-relaxed mt-10 text-center">
                        {t('cgv.page.privacyLink.text')}{' '}
                        <Link to="/confidentialite" className="text-primary hover:underline">{t('cgv.page.privacyLink.link')}</Link>.
                    </p>
                </div>
            </main>
            {!isNative && <Footer />}
        </>
    );
};

export default SubscriptionTermsPage;
